import React, {createRef, useCallback, useEffect, useRef, useState} from "react";
import TextField from "@material-ui/core/TextField";
import {DialogContent, DialogTitle, TextareaAutosize} from "@material-ui/core";
import Button from "@material-ui/core/Button";
import z from "zod";
import {useForm} from "react-hook-form";
import {zodResolver} from "@hookform/resolvers/zod";
import {
    useDeleteDriverListContent,
    useGetDriverContent,
    useGetDriverListContent,
    usePatchDriverContent,
    usePatchDriverListContent,
    usePostDriverListContent
} from "../../../../Data/ApiHooks/UseContentHooks";
import useSnackbar from "../../../../CustomHooks/useSnackbar";
import PropTypes from "prop-types";
import Dialog from "@material-ui/core/Dialog";
import PlaceholderImage from "../../../../Assets/placeholder-image.png";

const DriverEditorTab = ({ setShowEditedAlert }) => {

    const driverTypeInputRefs = useRef([]);

    const { data, refetch } = useGetDriverContent();
    const [driverList, setDriverList] = useState([]);
    const [openModal, setOpenModal] = useState(false);
    const [vehicleIndexToDelete, setVehicleIndexToDelete] = useState(null);

    // region API Hooks
    const { mutate, isLoading } = usePatchDriverContent({ onSuccess: () => onPatchDriverContentSuccess(), onError: (error) => onPatchDriverContentError(error) });
    const { data: driverListData, refetch: refetchDriverListData } = useGetDriverListContent();
    const { mutate: postVehicleListContent } = usePostDriverListContent({ onSuccess: () => onUpdateDriverContentSuccess(), onError: (error) => onUpdateDriverContentError(error) });
    const { mutate: patchVehicleListContent } = usePatchDriverListContent({ onSuccess: () => onUpdateDriverContentSuccess(), onError: (error) => onUpdateDriverContentError(error) });
    const { mutate: deleteVehicleListContent } = useDeleteDriverListContent({ onSuccess: () => onUpdateDriverContentSuccess(), onError: (error) => onUpdateDriverContentError(error) });
    // endregion

    const formSchema = z.object({
        title: z.string().min(1, "Title is required"),
        description: z.string().min(1, "Description is required"),
    });

    // region Custom hooks

    const {
        register,
        handleSubmit,
        formState: { errors, isDirty },
        reset,
    } = useForm({
        resolver: zodResolver(formSchema),
    });
    const { showSnackBar } = useSnackbar();

    // endregion

    // region Queries callback functions

    function onPatchDriverContentSuccess() {
        showSnackBar("Driver content updated successfully", "success");
        refetch();
    }

    function onPatchDriverContentError(error) {
        showSnackBar(error, "error");
    }

    function onUpdateDriverContentSuccess() {
        showSnackBar("Driver content updated successfully", "success");
        refetchDriverListData().then();
    }

    function onUpdateDriverContentError(error) {
        showSnackBar(error, "error");
    }

    // endregion

    // region Functions

    const createImageUrl = useCallback((imageUrl) => {
        if (!imageUrl) {
            return PlaceholderImage;
        }

        return imageUrl;
    }, [])

    // endregion

    // region Handler functions

    const handleAddDriver = useCallback(() => {
        const newDriver = {
            id: `new-${Date.now()}`,
            title: "",
            description: "",
            properties: [],
            image: PlaceholderImage
        };
        setDriverList([newDriver, ...driverList]);
    }, [driverList]);

    const handleAddDriverProperty = useCallback((e, index) => {
        if (e.key === "Enter" && e.target.value.trim()) {
            const updatedVehicleList = [...driverList];
            updatedVehicleList[index] = {
                ...updatedVehicleList[index],
                properties: [...updatedVehicleList[index].properties, e.target.value.trim()]
            };
            setDriverList(updatedVehicleList);
            e.target.value = "";
        }
    }, [driverList]);


    const handleDeleteDriverProperty = useCallback((propertyIndex, vehicleIndex) => {
        const updatedVehicleList = [...driverList];
        updatedVehicleList[vehicleIndex] = {
            ...updatedVehicleList[vehicleIndex],
            properties: updatedVehicleList[vehicleIndex].properties.filter((_, i) => i !== propertyIndex)
        };
        setDriverList(updatedVehicleList);
    }, [driverList]);

    const handleDriverTitleChange = useCallback((e, index) => {
        const updatedDriverList = [...driverList];
        updatedDriverList[index] = {
            ...updatedDriverList[index],
            title: e.target.value
        };
        setDriverList(updatedDriverList);
    }, [driverList]);

    const handleDriverImageChange = useCallback((e, index) => {
        const file = e.target.files[0];
        if (file) {
            const updatedVehicleList = [...driverList];
            updatedVehicleList[index] = {
                ...updatedVehicleList[index],
                image: URL.createObjectURL(file),
                imageFile: file
            };
            setDriverList(updatedVehicleList);
        }
    }, [driverList]);

    const handleDriverDeletion = useCallback((driverIndex) => {
        const updatedVehicleList = [...driverList];
        updatedVehicleList.splice(driverIndex, 1);
        setDriverList(updatedVehicleList);

        const vehicleId = driverList[driverIndex].id;

        if (!vehicleId.includes("new-")) {
            deleteVehicleListContent(vehicleId);
        }

        setOpenModal(false);
    }, [deleteVehicleListContent, driverList]);

    const handleDriverListSubmit = useCallback(() => {
        driverList.forEach((driver) => {
            const formData = new FormData();
            formData.append("title", driver.title);
            formData.append("properties", JSON.stringify(driver.properties));
            if (driver.imageFile) {
                formData.append("image", driver.imageFile);
            }

            if (driver.id.includes("new-")) {
                postVehicleListContent(formData);
            } else {
                formData.append("id", driver.id);
                patchVehicleListContent(formData);
            }
        });
    }, [driverList, postVehicleListContent, patchVehicleListContent]);


    const handleOnSubmit = useCallback((formData) => {
        if (!data) {
            return;
        }

        const jsonData = {
            id: data.id,
            driver_service_title: formData.title || "",
            driver_description: formData.description || "",
        };

        mutate(jsonData);
        handleDriverListSubmit();
    }, [data, handleDriverListSubmit, mutate]);

    // endregion

    // region UseEffects hooks

    useEffect(() => {
        if (data) {
            reset({
                title: data.driver_service_title || "",
                description: data.driver_description || ""
            });
        }
    }, [data, reset]);

    useEffect(() => {
        if (isDirty) {
            setShowEditedAlert(isDirty);
        }
    }, [isDirty, setShowEditedAlert]);

    useEffect(() => {
        if (driverListData) {
            setDriverList(driverListData || []);
            driverTypeInputRefs.current = Array(driverListData?.length || 0)
                .fill()
                .map((_, i) => driverTypeInputRefs.current[i] || createRef());
        }
    }, [driverListData]);

    useEffect(() => {
        driverTypeInputRefs.current = Array(driverList.length)
            .fill()
            .map((_, i) => driverTypeInputRefs.current[i] || createRef());
    }, [driverList.length]);
    // endregion

    return (
        <>
            <Dialog open={openModal} onClose={() => setOpenModal(false)}>
                <DialogTitle className="dialog-title">Do you want to delete this driver?</DialogTitle>
                <DialogContent className="dialog-content-container">
                    <div className="delete-button-container">
                        <Button className="delete-button" onClick={() => handleDriverDeletion(vehicleIndexToDelete)}>
                            Yes
                        </Button>
                    </div>
                    <Button className="vehicle-delete-button-no" onClick={() => setOpenModal(false)}>No</Button>
                </DialogContent>
            </Dialog>
            <form className="driver-editor-form" onSubmit={(e) => {
                e.preventDefault();
                handleSubmit(handleOnSubmit)(e);
            }}>
                <div className="button-container">
                    <Button
                        type="submit"
                        className="submit-button"
                        disabled={isLoading}
                    >
                        Update
                    </Button>
                </div>
                <div className="form-container">
                    <div className="form-field">
                        <label>Driver service title</label>
                        <TextField
                            {...register("title")}
                            className="text-input"
                            error={!!errors.title}
                            fullWidth
                            helperText={errors.title?.message}
                            placeholder="Enter title"
                        />
                    </div>

                    <div className="form-field">
                        <label>Driver description</label>
                        <TextareaAutosize
                            {...register("description")}
                            className="textarea"
                            error={!!errors.description}
                            placeholder="Enter description"
                            minRows={4}
                        />
                    </div>

                    <div className="button-container">
                        <Button
                            type="button"
                            className="add-vehicle-button"
                            onClick={handleAddDriver}
                        >
                            Add Driver
                        </Button>
                    </div>

                    <div className="vehicle-list-container">
                        {driverList.length === 0 ? "No vehicle available!" : driverList.map((vehicle, index) => {
                            return (
                                <div className="vehicle-container" key={vehicle.id}>
                                    <div className="home-editor-form-input-container">
                                        <span className="field-title">Driver title</span>
                                        <TextField
                                            className="home-editor-form-input"
                                            helperText={errors.vehicleServiceTitle?.message}
                                            error={!!errors.vehicleServiceTitle}
                                            fullWidth
                                            placeholder="Enter vehicle service title"
                                            value={vehicle.title}
                                            onChange={(e) => handleDriverTitleChange(e, index)}
                                        />

                                        <div className="form-field">
                                            <label>Driver properties</label>
                                            <div className="text-input-container" style={{ display: "flex", gap: "8px" }}>
                                                <TextField
                                                    className="text-input"
                                                    fullWidth
                                                    inputRef={(el) => (driverTypeInputRefs.current[index] = el)}
                                                    placeholder="Press Enter to add driver type"
                                                    onKeyDown={(e) => handleAddDriverProperty(e, index)}
                                                />
                                                <Button
                                                    onClick={() => {
                                                        const inputRef = driverTypeInputRefs.current[index];
                                                        if (inputRef?.value?.trim()) {
                                                            const updatedVehicleList = [...driverList];
                                                            updatedVehicleList[index] = {
                                                                ...updatedVehicleList[index],
                                                                properties: [...updatedVehicleList[index].properties, inputRef.value.trim()]
                                                            };
                                                            setDriverList(updatedVehicleList);
                                                            inputRef.value = "";
                                                        }
                                                    }}
                                                >
                                                    Add
                                                </Button>
                                            </div>
                                            <div className="driver-types-list">
                                                {vehicle.properties && vehicle.properties.map((type, propertyIndex) => (
                                                    <div key={propertyIndex} className="driver-type-item">
                                                        <span>{type}</span>
                                                        <Button className="delete-button" onClick={() => handleDeleteDriverProperty(propertyIndex, index)}>Delete
                                                        </Button>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>

                                    <input
                                        type="file"
                                        accept="image/*"
                                        style={{ display: "none" }}
                                        id={`partner-image-upload-${vehicle.id}`}
                                        onChange={(e) => handleDriverImageChange(e, index)}
                                    />
                                    <label htmlFor={`partner-image-upload-${vehicle.id}`}>
                                        <img
                                            src={createImageUrl(vehicle.image)}
                                            alt={`Gallery image ${index + 1}`}
                                            style={{ width: "100%", height: "200px", objectFit: "cover", cursor: "pointer" }}
                                        />
                                    </label>

                                    <div className="delete-button-container">
                                        <Button className="delete-button" onClick={() => {
                                            setOpenModal(true);
                                            setVehicleIndexToDelete(index);
                                        }}>
                                            Delete driver
                                        </Button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </form>
        </>
    );
};

DriverEditorTab.propTypes = {
    setShowEditedAlert: PropTypes.func
};

export default DriverEditorTab;
